/**
 * Where a client lands after adding an account.
 *
 * Shared because both clients answer it from the same server value and must not
 * drift: web reads the thread id off the OAuth result page's query string,
 * native off the finalize response body.
 *
 * Only the validation is shared. Building the route is not: web wants a path
 * string and native a typed expo-router href, and each already has its own way
 * of saying it.
 */

/**
 * The setup thread id a server told us to land in, or undefined if it did not
 * give us a usable one.
 *
 * Validating rather than trusting is the point. Web's result page reads its
 * params straight off the query string with nothing proving the page was
 * reached from our own redirect, so this value is attacker-supplied in a
 * crafted link. Interpolating it into a path unchecked turns `//evil.com` into
 * an off-site redirect; a positive integer cannot, and a forged one resolves to
 * nothing because the thread routes are account-scoped on the server.
 *
 * Takes `unknown` so the string a query param yields and the number JSON yields
 * both arrive here rather than each caller narrowing its own way.
 */
export function parseOnboardingThreadId(value: unknown): number | undefined {
  if (typeof value !== "number" && typeof value !== "string") return undefined;
  // `Number("")` is 0 and `Number(" 7 ")` is 7, so an empty or padded param
  // must not become a route. Numeric input skips the pattern and is checked by
  // the integer test below.
  if (typeof value === "string" && !/^\d+$/.test(value)) return undefined;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return undefined;
  return parsed;
}
